"use client";

import { memo, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  Box,
  Button,
  Grid,
  HStack,
  Menu,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithMenuItem from "@/shared/ui/actions/WithMenuItem";
import { useColorMode } from "@/shared/ui/color-mode";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import { useUserChoices } from "@/state/globalStore";
import { TelegramLinkDialog, useTelegramLink } from "@/features/telegram";
import { useNeedsAnchorCount } from "@/features/profile-shaping";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_LAUNCHER_ACTIONS,
  type ProfileShapingLauncherAction,
  type ProfileShapingLauncherActionId,
} from "@/features/profile-shaping";
import usePrivateLogout from "./hooks/usePrivateLogout";
import { useActionMenuSlots } from "./ActionMenuContributionProvider";
import type {
  ActionMenuAction,
  ActionMenuActionTone,
  ActionMenuSection,
} from "./actionMenu.types";

interface ActionMenuPanelProps {
  variant: "mobile" | "desktop";
  collapsed?: boolean;
}

type NeedsAnchorCount = ReturnType<typeof useNeedsAnchorCount>;

type ProfileShapingActionPresentation = {
  label: string;
  humanReadable: string;
  disabled: boolean;
};

const DESKTOP_TONE_STYLES: Record<
  ActionMenuActionTone,
  { color?: string; hoverBg?: string }
> = {
  default: {},
  success: {
    color: "fg.success",
    hoverBg: "bg.success",
  },
  warning: {
    color: "fg.warning",
    hoverBg: "bg.warning",
  },
  error: {
    color: "fg.error",
    hoverBg: "bg.error",
  },
};

const STARTER_PROFILE_SHAPING_ACTION_KEYS = new Set([
  "profile_shaping_source",
  "profile_shaping_focus",
]);

function formatNeedsAnchorLabel(count: number) {
  return `${PROFILE_SHAPING_LABELS.reviewNeedsAnchor} · ${count.toLocaleString()}`;
}

function getProfileShapingActionPresentation(
  action: ProfileShapingLauncherAction,
  needsAnchorCount: NeedsAnchorCount,
): ProfileShapingActionPresentation {
  const isKnownEmptyNeedsAnchorAction =
    action.id === "needs_anchor" &&
    needsAnchorCount.hasKnownCount &&
    needsAnchorCount.count === 0;
  const needsAnchorLabel =
    action.id === "needs_anchor" &&
    needsAnchorCount.hasKnownCount &&
    typeof needsAnchorCount.count === "number" &&
    needsAnchorCount.count > 0
      ? formatNeedsAnchorLabel(needsAnchorCount.count)
      : action.label;

  return {
    label: isKnownEmptyNeedsAnchorAction
      ? PROFILE_SHAPING_LABELS.allThoughtsAnchored
      : needsAnchorLabel,
    humanReadable: isKnownEmptyNeedsAnchorAction
      ? PROFILE_SHAPING_LABELS.allThoughtsAnchored
      : action.humanReadable,
    disabled: isKnownEmptyNeedsAnchorAction,
  };
}

function ActionMenuActionIcon({ action }: { action: ActionMenuAction }) {
  if (!action.icon) {
    return null;
  }

  return <GenericIcon icon={action.icon} size={iconSizePresets.menuItem} />;
}

function MobileQuickActions({ actions }: { actions: ActionMenuAction[] }) {
  return (
    <Grid templateColumns="repeat(4, minmax(0, 1fr))" gap="1">
      {actions.map((action) => (
        <WithMenuItem
          key={action.key}
          value={action.key}
          humanReadable={action.humanReadable}
          width="14"
          gap="1"
          flexDirection="column"
          justifyContent="center"
          tone={action.tone === "default" ? undefined : action.tone}
          onClick={action.onSelect}
          disabled={action.disabled}
          closeOnSelect={action.closeOnSelect}
          hide={action.hidden}
        >
          <ActionMenuActionIcon action={action} />
          {action.label}
        </WithMenuItem>
      ))}
    </Grid>
  );
}

function MobileActionSection({
  section,
  showSeparator,
}: {
  section: ActionMenuSection;
  showSeparator: boolean;
}) {
  const visibleActions = section.actions.filter((action) => !action.hidden);

  if (!section.title) {
    return null;
  }

  return (
    <Box>
      {showSeparator ? <Menu.Separator /> : null}
      <Menu.ItemGroup>
        <Menu.ItemGroupLabel>{section.title}</Menu.ItemGroupLabel>
        {visibleActions.length === 0 ? (
          section.emptyState ? (
            <GenericEmptyComponent
              title={section.emptyState.title}
              message={section.emptyState.message}
              icon={section.emptyState.icon}
              variant="inline"
            />
          ) : null
        ) : (
          visibleActions.map((action) => (
            <WithMenuItem
              key={action.key}
              value={action.key}
              humanReadable={action.humanReadable}
              tone={action.tone === "default" ? undefined : action.tone}
              onClick={action.onSelect}
              disabled={action.disabled}
              closeOnSelect={action.closeOnSelect}
            >
              <ActionMenuActionIcon action={action} />
              {action.label}
            </WithMenuItem>
          ))
        )}
      </Menu.ItemGroup>
    </Box>
  );
}

function DesktopQuickActions({
  actions,
  collapsed = false,
}: {
  actions: ActionMenuAction[];
  collapsed?: boolean;
}) {
  return (
    <Stack gap="3">
      {collapsed ? null : (
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="0.08em"
          color="moveon.text.muted"
        >
          Quick Actions
        </Text>
      )}
      <Grid
        templateColumns={collapsed ? "1fr" : "repeat(2, minmax(0, 1fr))"}
        gap="2"
      >
        {actions
          .filter((action) => !action.hidden)
          .map((action) => {
            const toneStyles = DESKTOP_TONE_STYLES[action.tone ?? "default"];

            return (
              <Button
                key={action.key}
                variant="ghost"
                flexDirection="column"
                gap={collapsed ? "0" : "1"}
                justifyContent="center"
                alignItems="center"
                minH={collapsed ? "12" : "20"}
                h="auto"
                py={collapsed ? "2" : "3"}
                onClick={action.onSelect}
                disabled={action.disabled}
                title={action.humanReadable}
                aria-label={action.humanReadable}
                color={toneStyles.color}
                _hover={
                  toneStyles.hoverBg
                    ? {
                        bg: toneStyles.hoverBg,
                        color: toneStyles.color,
                      }
                    : undefined
                }
              >
                <ActionMenuActionIcon action={action} />
                {collapsed ? null : <Text fontSize="sm">{action.label}</Text>}
              </Button>
            );
          })}
      </Grid>
    </Stack>
  );
}

function TelegramDialogSlot({
  telegramLinkData,
  isOpen,
  onCloseAction,
}: {
  telegramLinkData: ReturnType<typeof useTelegramLink>["telegramLinkData"];
  isOpen: boolean;
  onCloseAction: () => void;
}) {
  if (!telegramLinkData) {
    return null;
  }

  return (
    <TelegramLinkDialog
      isOpen={isOpen}
      onCloseAction={onCloseAction}
      linkData={telegramLinkData}
    />
  );
}

function ActionMenuDesktopButton({
  action,
  collapsed = false,
}: {
  action: ActionMenuAction;
  collapsed?: boolean;
}) {
  const toneStyles = DESKTOP_TONE_STYLES[action.tone ?? "default"];

  if (action.hidden) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      justifyContent={collapsed ? "center" : "flex-start"}
      onClick={action.onSelect}
      disabled={action.disabled}
      title={action.humanReadable}
      aria-label={action.humanReadable}
      color={toneStyles.color}
      _hover={
        toneStyles.hoverBg
          ? {
              bg: toneStyles.hoverBg,
              color: toneStyles.color,
            }
          : undefined
      }
      whiteSpace="normal"
      h="auto"
      minH={collapsed ? "12" : "10"}
      py="2"
      px={collapsed ? "2" : "3"}
    >
      <HStack
        gap={collapsed ? "0" : "3"}
        align="center"
        justify={collapsed ? "center" : "flex-start"}
        width="full"
      >
        {action.icon && (
          <GenericIcon icon={action.icon} size={iconSizePresets.inline} />
        )}
        {collapsed ? null : (
          <Text fontSize="sm" textAlign="left">
            {action.label}
          </Text>
        )}
      </HStack>
    </Button>
  );
}

function ActionMenuDesktopSection({
  section,
  collapsed = false,
}: {
  section: ActionMenuSection;
  collapsed?: boolean;
}) {
  const visibleActions = section.actions.filter((action) => !action.hidden);

  if (collapsed && visibleActions.length === 0) {
    return null;
  }

  return (
    <Stack gap="2">
      {section.title && !collapsed ? (
        <Text
          px="3"
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="0.08em"
          color="moveon.text.muted"
        >
          {section.title}
        </Text>
      ) : null}

      {visibleActions.length === 0 ? (
        section.emptyState ? (
          <Box px="3">
            <GenericEmptyComponent
              title={section.emptyState.title}
              message={section.emptyState.message}
              icon={section.emptyState.icon}
              variant="inline"
            />
          </Box>
        ) : null
      ) : (
        <Stack gap="1">
          {visibleActions.map((action) => (
            <ActionMenuDesktopButton
              key={action.key}
              action={action}
              collapsed={collapsed}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ActionMenuPanel({ variant, collapsed = false }: ActionMenuPanelProps) {
  const router = useRouter();
  const { toggleColorMode, colorMode } = useColorMode();
  const { handleLogout } = usePrivateLogout();
  const {
    handleTelegramLinkClick,
    telegramLinkData,
    isTelegramLinkDialogOpen,
    closeTelegramLinkDialog,
  } = useTelegramLink();
  const {
    activePrototypeCategory,
    setActiveStudioContent,
    setActiveStudioGrouping,
    setActiveStudioExportLock,
    setActiveStudioMaintenanceMode,
    setInterimState,
    resetInterimState,
  } = useUserChoices();
  const { quickActions, featureSections, utilityActions } =
    useActionMenuSlots();
  const needsAnchorCount = useNeedsAnchorCount();

  const handleProfileShapingLauncherSelect = useCallback(
    (actionId: ProfileShapingLauncherActionId) => {
      if (actionId === "source") {
        setActiveStudioMaintenanceMode("none");
        resetInterimState("profile_shaping_guidance");
        setInterimState("add_origin", { isOpen: true });
        return;
      }

      if (actionId === "decompose_cv") {
        setActiveStudioMaintenanceMode("none");
        resetInterimState("profile_shaping_guidance");
        setInterimState("parse_resume", { isOpen: true });
        return;
      }

      if (actionId === "review") {
        setActiveStudioMaintenanceMode("none");
        setActiveStudioExportLock(false);
        resetInterimState("profile_shaping_guidance");
        setInterimState("suggested_thoughts_review", {
          isOpen: true,
          value: {
            prototype: activePrototypeCategory,
          },
        });
        router.push("/studio");
        return;
      }

      if (actionId === "focus") {
        setActiveStudioMaintenanceMode("none");
        resetInterimState("profile_shaping_guidance");
        setInterimState("add_target", { isOpen: true });
        return;
      }

      if (actionId === "needs_anchor") {
        resetInterimState("profile_shaping_guidance");
        setInterimState("needs_anchor_review", { isOpen: true });
        return;
      }

      setActiveStudioMaintenanceMode("none");
      setActiveStudioContent("confirmed");
      setActiveStudioGrouping("by_situation");
      setActiveStudioExportLock(false);
      setInterimState("profile_shaping_guidance", {
        isOpen: true,
        value: "manual_add",
      });
      router.push("/studio");
    },
    [
      resetInterimState,
      activePrototypeCategory,
      router,
      setActiveStudioContent,
      setActiveStudioExportLock,
      setActiveStudioGrouping,
      setActiveStudioMaintenanceMode,
      setInterimState,
    ],
  );

  const handleAddSituationSelect = useCallback(() => {
    resetInterimState("profile_shaping_guidance");
    setInterimState("add_situation", { isOpen: true });
  }, [resetInterimState, setInterimState]);

  const handleCollectFeedbackSelect = useCallback(() => {
    resetInterimState("profile_shaping_guidance");
    setInterimState("feedback_share", { isOpen: true });
  }, [resetInterimState, setInterimState]);

  const profileShapingLauncherActions = useMemo<ActionMenuAction[]>(
    () =>
      PROFILE_SHAPING_LAUNCHER_ACTIONS.map((action) => {
        const presentation = getProfileShapingActionPresentation(
          action,
          needsAnchorCount,
        );

        return {
          key: `profile_shaping_${action.id}`,
          label: presentation.label,
          humanReadable: presentation.humanReadable,
          icon: action.icon,
          disabled: presentation.disabled,
          closeOnSelect: true,
          onSelect: () => {
            if (presentation.disabled) {
              return;
            }

            handleProfileShapingLauncherSelect(action.id);
          },
        };
      }),
    [handleProfileShapingLauncherSelect, needsAnchorCount],
  );

  const addCareerSituationAction = useMemo<ActionMenuAction>(
    () => ({
      key: "profile_shaping_add_situation",
      label: PROFILE_SHAPING_LABELS.addCareerSituation,
      humanReadable: PROFILE_SHAPING_LABELS.addSituation,
      icon: "situation",
      closeOnSelect: true,
      onSelect: handleAddSituationSelect,
    }),
    [handleAddSituationSelect],
  );

  const collectFeedbackAction = useMemo<ActionMenuAction>(
    () => ({
      key: "profile_shaping_collect_feedback",
      label: "Collect feedback",
      humanReadable: "Collect professional feedback from another person",
      icon: "collect_feedback",
      closeOnSelect: true,
      onSelect: handleCollectFeedbackSelect,
    }),
    [handleCollectFeedbackSelect],
  );

  const profileShapingStarterActions = useMemo<ActionMenuAction[]>(
    () => {
      const sourceAction = profileShapingLauncherActions.find(
        (action) => action.key === "profile_shaping_source",
      );
      const focusAction = profileShapingLauncherActions.find(
        (action) => action.key === "profile_shaping_focus",
      );

      return [
        sourceAction,
        addCareerSituationAction,
        focusAction,
      ].filter((action): action is ActionMenuAction => Boolean(action));
    },
    [addCareerSituationAction, profileShapingLauncherActions],
  );

  const profileShapingFollowUpActions = useMemo<ActionMenuAction[]>(
    () => [
      ...profileShapingLauncherActions.filter(
        (action) => !STARTER_PROFILE_SHAPING_ACTION_KEYS.has(action.key),
      ),
      collectFeedbackAction,
      ...utilityActions,
    ],
    [collectFeedbackAction, profileShapingLauncherActions, utilityActions],
  );

  const accountSettingsActions = useMemo<ActionMenuAction[]>(
    () => [
      {
        key: "edit_personal",
        label: "Personal",
        humanReadable: "Edit personal information",
        icon: "personalize",
        onSelect: () => {
          setInterimState("edit_personal", { isOpen: true });
        },
      },
      {
        key: "link_telegram",
        label: "Telegram",
        humanReadable: "Link Telegram account",
        icon: "telegram",
        closeOnSelect: false,
        onSelect: () => {
          void handleTelegramLinkClick();
        },
      },
      {
        key: "edit_output_settings",
        label: "Settings",
        humanReadable: "Edit profile export settings",
        icon: "settings_print",
        onSelect: () => {
          setInterimState("edit_output_settings", { isOpen: true });
        },
      },
      {
        key: "logout",
        label: "Logout",
        humanReadable: "Log out",
        icon: "log_out",
        tone: "error",
        onSelect: handleLogout,
      },
    ],
    [handleLogout, handleTelegramLinkClick, setInterimState],
  );

  const shellActionSections = useMemo<ActionMenuSection[]>(
    () => [
      {
        key: "profile_shaping",
        title: PROFILE_SHAPING_LABELS.flow,
        actions: profileShapingStarterActions,
      },
      {
        key: "profile_shaping_review",
        title: "Review and maintain",
        actions: profileShapingFollowUpActions,
      },
      ...featureSections,
      {
        key: "utility",
        title: "Account & settings",
        actions: accountSettingsActions,
      },
    ],
    [
      accountSettingsActions,
      featureSections,
      profileShapingFollowUpActions,
      profileShapingStarterActions,
    ],
  );

  const topQuickActions = useMemo<ActionMenuAction[]>(
    () => [
      {
        key: "color_mode",
        label: "Color",
        humanReadable: "Toggle dark mode",
        icon: colorMode === "dark" ? "color_mode_dark" : "color_mode_light",
        closeOnSelect: true,
        onSelect: toggleColorMode,
      },
      ...quickActions,
      {
        key: "profile_stats",
        label: "Stats",
        humanReadable: "View profile statistics",
        icon: "insight",
        closeOnSelect: true,
        onSelect: () => {
          setInterimState("profile_stats", { isOpen: true });
        },
      },
    ],
    [colorMode, quickActions, setInterimState, toggleColorMode],
  );

  if (variant === "mobile") {
    return (
      <>
        <Stack gap="0">
          <MobileQuickActions actions={topQuickActions} />

          <Menu.Separator />

          {shellActionSections.map((section, index) => (
            <MobileActionSection
              key={section.key}
              section={section}
              showSeparator={index > 0}
            />
          ))}
        </Stack>
        <TelegramDialogSlot
          telegramLinkData={telegramLinkData}
          isOpen={isTelegramLinkDialogOpen}
          onCloseAction={closeTelegramLinkDialog}
        />
      </>
    );
  }

  return (
    <>
      <Stack
        gap="5"
        p={collapsed ? "2" : "4"}
        height="100%"
        bg="moveon.surface.bg"
        color="moveon.surface.fg"
      >
        <DesktopQuickActions actions={topQuickActions} collapsed={collapsed} />

        <Separator />

        <Stack gap="5" flex="1" overflowY="auto">
          {shellActionSections.map((section) => (
            <ActionMenuDesktopSection
              key={section.key}
              section={section}
              collapsed={collapsed}
            />
          ))}
        </Stack>
      </Stack>
      <TelegramDialogSlot
        telegramLinkData={telegramLinkData}
        isOpen={isTelegramLinkDialogOpen}
        onCloseAction={closeTelegramLinkDialog}
      />
    </>
  );
}

export default memo(ActionMenuPanel);
